"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

export default function HomepageCTA() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #5EBFB3 0%, #4DA79D 50%, #3D8A82 100%)" }}
    >
      {/* Decorative circles */}
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none opacity-20"
        style={{ background: "radial-gradient(circle, white 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full pointer-events-none opacity-15"
        style={{ background: "radial-gradient(circle, white 0%, transparent 70%)" }}
      />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
        <div className="scroll-fade-up mb-5">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/15 text-white">
            <Icon name="RocketLaunchIcon" size={12} />
            Free to join · No credit card
          </span>
        </div>

        <h2 className="scroll-fade-up text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08] mb-6">
          Give your pet the care
          <br />
          they deserve.
        </h2>

        <p className="scroll-fade-up text-white/80 text-lg leading-relaxed max-w-xl mx-auto mb-10">
          Join 2,400+ pet owners who've already created their pet's profile. It's free, it's fast, and your pet will thank you.
        </p>

        <div className="scroll-fade-up flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/for-pet-owners"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#4DA79D] font-bold font-heading text-base py-4 px-8 rounded-full hover:bg-teal-50 transition-all duration-200 hover:scale-105 hover:shadow-lg"
            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
          >
            <Icon name="UserPlusIcon" size={20} />
            Create Free Pet Profile
          </Link>
          <Link href="/for-businesses" className="btn-ghost-white text-base py-4 px-8 transition-all duration-200 hover:scale-105 hover:shadow-lg">
            <Icon name="BuildingOfficeIcon" size={20} />
            Apply as Partner Business
          </Link>
        </div>

        <p className="scroll-fade-up text-white/50 text-sm mt-8">
          Already have 94 million US households with pets waiting to connect with you.
        </p>
      </div>
    </section>
  );
}